import React from 'react';
import type { DividendControlsData } from '@app-types/performanceDividend';
import previousVectorIcon from '../../../assets/images/previous_vector.svg';
import nextVectorIcon from '../../../assets/images/next_vector.svg';

export interface DividendControlsProps {
  data: DividendControlsData;
  nodeId?: string;
  selectedPeriod?: string;
  onPeriodChange?: (value: string) => void;
  /** Mobile chart page range label, e.g. `21/03 – 25/03`. */
  pageRangeLabel: string;
  /** Mobile chart page label, e.g. `2/2`. */
  pageLabel: string;
  /** Previous-page handler; invoked only when the button is enabled. */
  onPreviousPage?: () => void;
  /** Next-page handler; invoked only when the button is enabled. */
  onNextPage?: () => void;
  /** Disables the previous-page button (native `disabled`). */
  previousDisabled?: boolean;
  /** Disables the next-page button (native `disabled`). */
  nextDisabled?: boolean;
}

/**
 * Controls row for the Dividend Trends section.
 * The period selector updates chart/table data. The mobile pager paginates the
 * chart points only; range/page labels and disabled state are runtime-derived.
 */
const DividendControls: React.FC<DividendControlsProps> = ({
  data,
  nodeId,
  selectedPeriod = data.selectedPeriod,
  onPeriodChange,
  pageRangeLabel,
  pageLabel,
  onPreviousPage,
  onNextPage,
  previousDisabled = false,
  nextDisabled = false,
}) => (
  <div className="dividend-controls" data-node-id={nodeId}>
    <div className="dividend-controls__pager">
      <button
        type="button"
        className="dividend-controls__arrow-btn"
        aria-label="前のページ"
        onClick={onPreviousPage}
        disabled={previousDisabled}
      >
        <img
          className="dividend-controls__arrow-icon"
          src={previousVectorIcon}
          alt=""
          aria-hidden="true"
        />
      </button>
      <div className="dividend-controls__page-labels" aria-live="polite">
        <span className="dividend-controls__range-text">{pageRangeLabel}</span>
        <span className="dividend-controls__page-text">{pageLabel}</span>
      </div>
      <button
        type="button"
        className="dividend-controls__arrow-btn"
        aria-label="次のページ"
        onClick={onNextPage}
        disabled={nextDisabled}
      >
        <img
          className="dividend-controls__arrow-icon"
          src={nextVectorIcon}
          alt=""
          aria-hidden="true"
        />
      </button>
    </div>

    <div className="dividend-controls__dropdown">
      <select
        className="dividend-controls__select"
        aria-label="表示期間"
        value={selectedPeriod}
        onChange={(event) => onPeriodChange?.(event.target.value)}
      >
        {data.periodOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span className="dividend-controls__caret" aria-hidden="true">
        ▼
      </span>
    </div>
  </div>
);

export default DividendControls;
